import { getDisplayName, getStoreNameOrDisplayName, getTxImage } from '../../util/dataExtractors';
import { ORDER_STATUS_GROUPS, getPaidAt, isPaidOrder } from '../../util/orderStatusGroups';
import { getNetPayoutAmount } from '../../util/payoutAdjustments';
import { LINE_ITEM_ITEM } from '../../util/types';
import { STATUS_BADGE_VARIANTS, getStatusInfo } from '../../util/transactionStatusMap';
import {
  TX_TRANSITION_ACTOR_CUSTOMER,
  TX_TRANSITION_ACTOR_PROVIDER,
  getTransactionProcess,
} from '../../transactions/transaction';

const parseCartItems = transaction => {
  const raw = transaction?.attributes?.protectedData?.cartItems;
  if (!raw) {
    return [];
  }
  try {
    const items = typeof raw === 'string' ? JSON.parse(raw) : raw;
    return Array.isArray(items) ? items : [];
  } catch (e) {
    return [];
  }
};

// A cart item's photo is saved as a URL, or (older carts) as a Sharetribe image
const getCartItemImage = item => {
  const image = item?.firstImage;
  if (typeof image === 'string') {
    return image || null;
  }
  const variants = image?.attributes?.variants || {};
  const variant =
    variants['square-small'] || variants['listing-card'] || Object.values(variants)[0];
  return variant?.url || null;
};

/** The items in an order: every item of a cart order, or the order's one listing. */
export const getOrderItems = transaction => {
  const cartItems = parseCartItems(transaction).filter(item => item?.title);
  if (cartItems.length > 0) {
    return cartItems.map(item => ({
      title: item.title,
      image: getCartItemImage(item),
      quantity: Math.max(Number(item.stockCount) || 1, 1),
    }));
  }
  const lineItemQuantity = (transaction?.attributes?.lineItems || [])
    .filter(item => !item?.reversal && item?.code === LINE_ITEM_ITEM)
    .reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
  return [
    {
      title: transaction?.listing?.attributes?.title || '',
      image: getTxImage(transaction) || null,
      quantity: Math.max(lineItemQuantity, 1),
    },
  ];
};

/** Lower-case text a search on the orders list matches against: items, SKU, buyer and seller. */
export const getOrderSearchText = transaction =>
  [
    ...getOrderItems(transaction).map(item => item.title),
    transaction?.listing?.attributes?.publicData?.sku,
    getStoreNameOrDisplayName(transaction?.provider),
    getDisplayName(transaction?.customer),
    transaction?.customer?.attributes?.profile?.displayName,
    transaction?.provider?.attributes?.profile?.displayName,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

// The order's state as Sharetribe names it, e.g. "state/purchased-without-payout"
const getStateName = transaction => {
  try {
    const state = getTransactionProcess(transaction).getState(transaction);
    return state ? `state/${state}` : null;
  } catch (e) {
    return null;
  }
};

// The one next step shown on a card, if there is one
const getAction = (transaction, isSale) => {
  const state = getStateName(transaction);
  if (isSale && ORDER_STATUS_GROUPS.toShip.includes(state)) {
    return 'ship';
  }
  if (!isSale && ORDER_STATUS_GROUPS.shipped.includes(state)) {
    return 'confirmArrival';
  }
  return null;
};

/** What an order card shows. Amounts are in cents: what the seller earns, or what the buyer paid. */
export const getOrderCardData = (transaction, currentUser, intl, now = new Date()) => {
  const isSale = currentUser?.id?.uuid === transaction?.provider?.id?.uuid;
  const viewerRole = isSale ? TX_TRANSITION_ACTOR_PROVIDER : TX_TRANSITION_ACTOR_CUSTOMER;
  const items = getOrderItems(transaction);
  const otherParty = isSale ? transaction?.customer : transaction?.provider;
  const money = isSale ? transaction?.attributes?.payoutTotal : transaction?.attributes?.payinTotal;
  const statusInfo = getStatusInfo({ transaction, viewerRole, intl, now });

  return {
    id: transaction?.id?.uuid,
    isSale,
    routeName: isSale ? 'SaleDetailsPage' : 'OrderDetailsPage',
    title: items[0]?.title || '',
    moreCount: items.length - 1,
    itemCount: items.reduce((sum, item) => sum + item.quantity, 0),
    images: items
      .map(item => item.image)
      .filter(Boolean)
      .slice(0, 3),
    otherPartyName: isSale ? getDisplayName(otherParty) : getStoreNameOrDisplayName(otherParty),
    paidAt: isPaidOrder(transaction) ? getPaidAt(transaction) : null,
    amount: isSale ? getNetPayoutAmount(transaction) : money?.amount || 0,
    currency: money?.currency || 'USD',
    statusLabel: statusInfo.label,
    isLate: statusInfo.badgeVariant === STATUS_BADGE_VARIANTS.ERROR,
    badgeVariant: statusInfo.badgeVariant,
    action: getAction(transaction, isSale),
  };
};
